import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Heart, Shield, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const Footer = () => {
  const { setCurrentView } = useApp();
  
  const currentYear = new Date().getFullYear();
  
  const footerLinks = [
    {
      title: 'Our Apps',
      links: [
        { name: 'Story Crew', href: 'https://storycrew.divedeepdialogues.com', external: true },
        { name: 'Critical Thinking & Values (Soon)', href: '#', disabled: true },
        { name: 'Workplace Dynamics (Soon)', href: '#', disabled: true }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', action: () => setCurrentView('privacy-policy'), internal: true },
        { name: 'Terms & Conditions', action: () => setCurrentView('terms-conditions'), internal: true }
      ]
    },
    {
      title: 'About',
      links: [
        { name: 'Dive Deep Dialogues', href: 'https://divedeepdialogues.com', external: true, logo: true },
        { name: 'Contact', href: 'mailto:support@divedeepdialogues.com', external: true }
      ]
    }
  ];

  return (
    <footer className="relative mt-auto">
      <div className="bg-slate-900 border-t border-purple-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="space-y-4">
              <div className="space-y-3">
                {/* Inner Voice Logo & Title */}
                <div className="flex items-center gap-3">
                  <img 
                    src="/inner-voice.svg" 
                    alt="Inner Voice Logo" 
                    className="w-12 h-12"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h3 className="text-xl font-bold text-white">Inner Voice Vibe Edition</h3>
                    <p className="text-xs text-purple-300">A <span className="font-bold">Dive Deep Dialogues</span> App</p>
                  </div>
                </div>
                
                {/* App Series Text */}
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Part of the</span>
                  <a 
                    href="https://divedeepdialogues.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1"
                  >
                    Dive Deep into Self
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <span>app series</span>
                </div>
              </div>
              
              <p className="text-sm text-slate-400 leading-relaxed">
                Transform your self-talk with science-backed techniques and personalised insights.
              </p>
            </div>

            {/* Links Sections */}
            {footerLinks.map((section, index) => (
              <div key={index} className="space-y-4">
                <h4 className="text-sm font-semibold text-white uppercase tracking-wider">
                  {section.title}
                </h4>
                <ul className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      {link.disabled ? (
                        <span className="text-sm text-slate-500 cursor-not-allowed inline-flex items-center gap-1">
                          {link.name}
                        </span>
                      ) : link.internal ? (
                        <button
                          onClick={link.action}
                          className="text-sm text-slate-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1"
                        >
                          {link.name}
                        </button>
                      ) : (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-slate-400 hover:text-purple-300 transition-colors inline-flex items-center gap-2"
                        >
                          {link.logo && (
                            <img 
                              src="/ddd-logo.svg" 
                              alt="Dive Deep Dialogues Logo" 
                              className="w-12 h-12"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          {link.name}
                          {link.external && <ExternalLink className="w-3 h-3" />}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Made with Love */}
            <div className="space-y-4 text-left">
              <div className="text-sm text-slate-400">
                <p className="mb-2">Check out our <a href="https://innervoice.divedeepdialogues.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors font-bold">Inner Voice Adult Edition</a> for comprehensive personal development</p>
                <div className="flex items-center justify-start gap-2">
                  <span>Made with</span>
                  <Heart className="w-4 h-4 text-red-400 fill-current" />
                  <span>by</span>
                  <a 
                    href="https://divedeepdialogues.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Dive Deep Dialogues
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-8 pt-6 border-t border-slate-800">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-xs text-slate-500">
                © {currentYear} SILVER WOLF Productions. All rights reserved.
              </p>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  <span>Your data is 100% private</span>
                </div>
                <div className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  <span>No personal data tracking</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;